import { notFound, redirect } from '@tanstack/react-router';

import { ApiError } from '@/lib/treaty';

import { canRead, canWrite, canReadAnyDocuments, type Permissions } from './resource-permissions';

type ProjectRedirectParams = {
  type: 'project';
  projectId: string;
};

type EstateRedirectParams = {
  type: 'estate';
  estateId: string;
};

type RedirectParams = ProjectRedirectParams | EstateRedirectParams;

const redirectToDashboard = (params: RedirectParams) => {
  if (params.type === 'project') {
    throw redirect({
      to: '/projects/$projectId',
      params: {
        projectId: params.projectId,
      },
    });
  }

  throw redirect({
    to: '/estates/$estateId',
    params: {
      estateId: params.estateId,
    },
  });
};

const NO_ACCESS_STATUSES = [401, 403, 404];

// Without access a resource is shown as missing, so users can't tell whether it exists
export const withNotFoundOnNoAccess = async <T>(promise: Promise<T>) => {
  try {
    return await promise;
  } catch (error) {
    if (error instanceof ApiError && NO_ACCESS_STATUSES.includes(error.status)) {
      throw notFound();
    }

    throw error;
  }
};

export const requireGeneralRead = (permissions: Permissions | null | undefined) => {
  if (!canRead(permissions, 'general')) {
    throw notFound();
  }
};

export const requireMediaRead = (
  permissions: Permissions | null | undefined,
  params: RedirectParams
) => {
  if (!canRead(permissions, 'media')) {
    redirectToDashboard(params);
  }
};

export const requireMediaWrite = (
  permissions: Permissions | null | undefined,
  params: RedirectParams
) => {
  if (!canWrite(permissions, 'media')) {
    redirectToDashboard(params);
  }
};

export const requireDocumentsRead = (
  permissions: Permissions | null | undefined,
  params: RedirectParams
) => {
  if (!canReadAnyDocuments(permissions)) {
    redirectToDashboard(params);
  }
};
